// src/modules/assetManagement/createReturn.ts
// Round trip from a page to the Create Event / Create Product flows and back, with the new item handed over.
import type { NavigateFunction } from "react-router-dom";
import { BASE, ROUTES } from "./utils";

/** Where a create flow returns to, and the label for its back link */
export interface ReturnTo {
  path: string;
  label: string;
}

/** Router state a create flow receives */
export interface CreateFlowState {
  returnTo?: ReturnTo;
  /** Create Event only: open the create form right away */
  createEvent?: boolean;
  /** Create Event only: products to pre-select on the new event */
  prefillProductIds?: string[];
  /** Create Product only: the form's existing return path */
  returnPath?: string;
}

/** Router state the returning page receives */
export interface CreatedHandoff {
  createdEventId?: string;
  createdProductId?: string;
}

/** Router state for opening the Upload tab from another page */
export interface UploadEntryState {
  /** Where to go once the upload finishes; default is Asset Management → Assets */
  uploadReturnTo?: ReturnTo;
}

/** Router state Asset Management / a return page receives after an upload finishes */
export interface UploadDoneState {
  uploadedAssetIds: string[];
}

export const UPLOAD_RETURN: ReturnTo = { path: `${ROUTES.management}?tab=upload`, label: "Upload assets" };

export const goCreateEvent = (navigate: NavigateFunction, returnTo: ReturnTo, prefillProductIds: string[] = []) =>
  navigate(ROUTES.libraryEvents, { state: { createEvent: true, returnTo, prefillProductIds } satisfies CreateFlowState });

export const goCreateProduct = (navigate: NavigateFunction, returnTo: ReturnTo) =>
  navigate(`${BASE}/products/create`, {
    state: { returnTo, returnPath: returnTo.path } satisfies CreateFlowState,
  });

/** Open the Upload tab; after a successful upload the user returns to `returnTo` */
export const goUpload = (navigate: NavigateFunction, returnTo?: ReturnTo) =>
  navigate(`${ROUTES.management}?tab=upload`, { state: { uploadReturnTo: returnTo } satisfies UploadEntryState });

/** Send the user back with the item they just created */
export const returnWithCreated = (navigate: NavigateFunction, returnTo: ReturnTo, created: CreatedHandoff) =>
  navigate(returnTo.path, { state: created });